// =============================================================================
// Fase 5.5 — Step 5: RC Familiare (ex Legge Bersani)
// Solo se tipo_richiesta = "nuova". Skippato per rinnovo/fermo/contatto.
// Flow: verifica RC Fam? → (si) sinistri 5y? → intestatario donante? → donor info + libretto donante
// =============================================================================
import React, { useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DocUploader } from "./DocUploader";
import { DOC_IDS } from "../../hooks/useRcaWizard";

const PARENTELA_OPTS = [
  ["coniuge", "Coniuge"], ["padre", "Padre"], ["madre", "Madre"],
  ["figlio", "Figlio"], ["figlia", "Figlia"],
  ["fratello", "Fratello"], ["sorella", "Sorella"], ["altro", "Altro convivente"],
];

export function Step5Bersani({ state, setField, docs, setDoc, removeDoc }) {
  const [warnOpen, setWarnOpen] = useState(false);

  const chooseRcFam = (val) => {
    setField("rc_familiare", val);
    if (val !== "si") {
      // Reset campi Bersani se cambio idea
      setField("sinistri_5y_donante", null);
      setField("intestatario_donante", "");
      setField("donante_info", { nome: "", cognome: "", parentela: "", targa: "", tipo_veicolo: "auto" });
      // Soft-remove libretto donante
      removeDoc(DOC_IDS.LIB_CLASSE, false);
    }
  };

  const handleSinistri = (v) => {
    setField("sinistri_5y_donante", v);
    if (v === true) setWarnOpen(true);
  };

  const acceptWithoutBersani = () => {
    // Forza RC Fam a "no", pulisce doc bersani-related
    setField("rc_familiare", "no");
    setField("sinistri_5y_donante", null);
    setField("intestatario_donante", "");
    removeDoc(DOC_IDS.LIB_CLASSE, false);
    setWarnOpen(false);
  };

  const cancelWarning = () => {
    setField("sinistri_5y_donante", null);
    setWarnOpen(false);
  };

  return (
    <div className="space-y-4" data-testid="rca-step5">
      {/* v1.75.2 — Nuova domanda dedicata (SOLO se tipo_richiesta="nuova"):
          "Il veicolo è già assicurato con CU?" — precede il blocco RC Familiare. */}
      {state.tipo_richiesta === "nuova" && (
        <div className="mb-2" data-testid="rca-veicolo-classe-block">
          <h2 className="text-lg sm:text-xl font-semibold text-[#0F1F14]">
            Il veicolo è già assicurato e dispone di una classe di merito?
          </h2>
          <p className="text-sm text-[#4A5E51] mt-1 mb-3">
            Se sì, verrà utilizzata la classe di merito già maturata per il calcolo del preventivo.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { k: "si", t: "Sì", d: "Il veicolo è già assicurato e ha una classe di merito" },
              { k: "no", t: "No", d: "Il veicolo non è mai stato assicurato / non dispone di una classe di merito" },
            ].map((o) => {
              const on = state.veicolo_ha_classe_merito === o.k;
              return (
                <button
                  key={o.k}
                  type="button"
                  data-testid={`rca-veicolo-classe-${o.k}`}
                  onClick={() => {
                    setField("veicolo_ha_classe_merito", o.k);
                    // Coerenza: se sceglie "si" e rc_familiare non è ancora valorizzato,
                    // preseleziona "no" (utente può poi cambiare idea).
                    if (o.k === "si" && !state.rc_familiare) {
                      setField("rc_familiare", "no");
                    }
                  }}
                  className={`text-left rounded-xl border-2 p-4 transition-all ${
                    on
                      ? "border-[var(--brand-accent,#163321)] bg-[#163321]/5 ring-2 ring-[var(--brand-accent,#163321)]/20"
                      : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
                  }`}
                >
                  <div className="text-base font-semibold text-[#0F1F14]">{o.t}</div>
                  <div className="text-sm text-[#4A5E51] mt-1">{o.d}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Blocco RC Familiare — visibile SOLO dopo risposta a "veicolo_ha_classe_merito"
          (se tipo_richiesta="nuova"). Per gli altri tipi_richiesta (rinnovo/fermo/contatto)
          la sezione è già skippata dal parent RcaWizard. */}
      {(state.tipo_richiesta !== "nuova" || state.veicolo_ha_classe_merito) && (
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-[#0F1F14]">
          Vuoi verificare la possibilità di usufruire della RC Familiare, già Legge Bersani?
        </h2>
        <p className="text-sm text-[#4A5E51] mt-1">
          {state.veicolo_ha_classe_merito === "si"
            ? "La classe di merito attuale del veicolo verrà utilizzata. Puoi comunque valutare la Legge Bersani se hai un familiare con classe migliore."
            : state.veicolo_ha_classe_merito === "no"
              ? "Consigliato: la Legge Bersani ti permette di ereditare una classe migliore da un familiare convivente ed evitare di partire dalla 14ª (premio massimo)."
              : "Potrebbe farti risparmiare significativamente sul premio."}
        </p>
      </div>
      )}

      {/* Scelta iniziale sì/no/non so (visibile alle stesse condizioni del blocco RC Fam) */}
      {(state.tipo_richiesta !== "nuova" || state.veicolo_ha_classe_merito) && (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { k: "si", t: "Sì", d: "Voglio verificare l'applicabilità" },
          { k: "no", t: "No", d: "Preferisco partire dalla classe standard" },
          { k: "non_so", t: "Non lo so", d: "Fatemi verificare dall'agenzia" },
        ].map((o) => {
          const on = state.rc_familiare === o.k;
          return (
            <button
              key={o.k}
              type="button"
              onClick={() => chooseRcFam(o.k)}
              data-testid={`rca-rc-familiare-${o.k}`}
              className={`text-left rounded-xl border-2 p-3 transition-all ${
                on
                  ? "border-[var(--brand-accent,#163321)] bg-[#163321]/5 ring-2 ring-[var(--brand-accent,#163321)]/20"
                  : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
              }`}
            >
              <div className="text-sm font-semibold text-[#0F1F14]">{o.t}</div>
              <div className="text-[11px] text-[#4A5E51] mt-1">{o.d}</div>
            </button>
          );
        })}
      </div>
      )}

      {/* Se "non_so" → messaggio informativo */}
      {state.rc_familiare === "non_so" && (
        <div
          className="rounded-xl border-l-4 border-blue-400 bg-blue-50 p-4 text-sm text-blue-900"
          data-testid="rca-rc-fam-non-so-info"
        >
          Nessun problema. Invieremo la richiesta a un esperto dell'agenzia, che verificherà se puoi usufruire della RC Familiare.
        </div>
      )}

      {/* Se "si" → info + domande */}
      {state.rc_familiare === "si" && (
        <>
          <div
            className="rounded-xl border-l-4 border-[var(--brand-accent,#163321)] bg-[#FAF9F5] p-4 text-sm text-[#0F1F14]"
            data-testid="rca-rc-fam-info"
          >
            <Info className="inline w-4 h-4 mr-1 text-[var(--brand-accent,#163321)]" />
            La RC Familiare permette, in alcuni casi, di utilizzare una classe di merito più favorevole
            maturata su un altro veicolo intestato al richiedente o a un familiare convivente.
            La possibilità di applicarla deve essere verificata dall'agenzia in base alla documentazione fornita.
          </div>

          {/* Q1: sinistri 5y */}
          <div className="rounded-2xl border border-[#E5E2DB] bg-white p-4 space-y-3">
            <Label className="text-sm font-medium text-[#0F1F14]">
              Ci sono stati sinistri con colpa negli ultimi 5 anni sul veicolo dove si recupera la classe?
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleSinistri(true)}
                data-testid="rca-sinistri-si"
                className={`rounded-xl border-2 p-3 transition-all ${
                  state.sinistri_5y_donante === true
                    ? "border-red-500 bg-red-50" : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
                }`}
              >
                <div className="text-sm font-semibold">Sì</div>
              </button>
              <button
                type="button"
                onClick={() => handleSinistri(false)}
                data-testid="rca-sinistri-no"
                className={`rounded-xl border-2 p-3 transition-all ${
                  state.sinistri_5y_donante === false
                    ? "border-[var(--brand-accent,#163321)] bg-[#163321]/5" : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
                }`}
              >
                <div className="text-sm font-semibold">No</div>
              </button>
            </div>
          </div>

          {/* Q2: intestatario donante (solo se sinistri=false) */}
          {state.sinistri_5y_donante === false && (
            <div className="rounded-2xl border border-[#E5E2DB] bg-white p-4 space-y-3">
              <Label className="text-sm font-medium text-[#0F1F14]">
                Chi è l'intestatario del veicolo da cui vuoi recuperare la classe di merito?
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { k: "dipendente", t: "Dipendente convenzionato" },
                  { k: "familiare", t: "Persona appartenente allo stato di famiglia" },
                ].map((o) => {
                  const on = state.intestatario_donante === o.k;
                  return (
                    <button
                      key={o.k}
                      type="button"
                      onClick={() => setField("intestatario_donante", o.k)}
                      data-testid={`rca-intestatario-donante-${o.k}`}
                      className={`text-left rounded-xl border-2 p-3 transition-all ${
                        on
                          ? "border-[var(--brand-accent,#163321)] bg-[#163321]/5 ring-2 ring-[var(--brand-accent,#163321)]/20"
                          : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
                      }`}
                    >
                      <div className="text-sm font-semibold text-[#0F1F14]">{o.t}</div>
                    </button>
                  );
                })}
              </div>

              {/* Donor info + libretto donante */}
              {state.intestatario_donante && (
                <div className="pt-3 border-t border-[#E5E2DB] space-y-3" data-testid="rca-donor-info-block">
                  <p className="text-[11px] font-semibold text-[#0F1F14]">
                    Dati del veicolo/persona da cui si recupera la classe
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Nome intestatario *</Label>
                      <Input
                        value={state.donante_info.nome}
                        onChange={(e) => setField("donante_info.nome", e.target.value)}
                        data-testid="rca-donor-nome"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Cognome intestatario *</Label>
                      <Input
                        value={state.donante_info.cognome}
                        onChange={(e) => setField("donante_info.cognome", e.target.value)}
                        data-testid="rca-donor-cognome"
                      />
                    </div>
                    {state.intestatario_donante === "familiare" && (
                      <div>
                        <Label className="text-xs">Parentela *</Label>
                        <Select
                          value={state.donante_info.parentela}
                          onValueChange={(v) => setField("donante_info.parentela", v)}
                        >
                          <SelectTrigger className="h-10 text-sm" data-testid="rca-donor-parentela">
                            <SelectValue placeholder="Seleziona" />
                          </SelectTrigger>
                          <SelectContent>
                            {PARENTELA_OPTS.map(([k, l]) => (
                              <SelectItem key={k} value={k}>{l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">Targa veicolo donante *</Label>
                      <Input
                        value={state.donante_info.targa}
                        onChange={(e) =>
                          setField(
                            "donante_info.targa",
                            e.target.value.toUpperCase().replace(/\s/g, "")
                          )
                        }
                        maxLength={10}
                        data-testid="rca-donor-targa"
                      />
                    </div>
                  </div>

                  <DocUploader
                    docId={DOC_IDS.LIB_CLASSE}
                    label="Libretto del veicolo da cui recuperare la classe"
                    description="Foto o scansione del libretto del veicolo donante."
                    required
                    currentDoc={docs[DOC_IDS.LIB_CLASSE]}
                    onUploaded={(id, p) => setDoc(id, { ...p, status: "caricato" })}
                    onCleared={(id) => removeDoc(id, false)}
                    testid="rca-doc-lib-classe"
                  />

                  {/* HOTFIX 27-lug — Stato di famiglia OBBLIGATORIO se intestatario=familiare
                       (già in computeRequiredDocs ma prima l'uploader non era visibile → Avanti bloccato) */}
                  {state.intestatario_donante === "familiare" && (
                    <DocUploader
                      docId={DOC_IDS.STATO_FAM}
                      label="Stato di famiglia"
                      description="Certificato di stato di famiglia che attesti la convivenza con l'intestatario del veicolo donante."
                      required
                      currentDoc={docs[DOC_IDS.STATO_FAM]}
                      onUploaded={(id, p) => setDoc(id, { ...p, status: "caricato" })}
                      onCleared={(id) => removeDoc(id, false)}
                      testid="rca-doc-stato-fam-bersani"
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Warning modal-like: sinistri con colpa */}
      {warnOpen && (
        <div
          className="rounded-2xl border-2 border-red-500 bg-red-50 p-4 space-y-3"
          data-testid="rca-warning-sinistri"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <p className="font-semibold">Con i sinistri con colpa la RC Familiare non si può applicare.</p>
              <p className="mt-1">Vuoi continuare senza RC Familiare?</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={acceptWithoutBersani}
              data-testid="rca-warning-continua-no-bersani"
              className="px-4 py-2 rounded-lg bg-[var(--brand-accent,#163321)] text-white text-sm font-medium hover:opacity-90"
            >
              Sì, continua senza RC Familiare
            </button>
            <button
              type="button"
              onClick={cancelWarning}
              data-testid="rca-warning-annulla"
              className="px-4 py-2 rounded-lg border border-[#E5E2DB] bg-white text-sm text-[#4A5E51] hover:border-[#4A5E51]"
            >
              No, annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
